import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, HeartPulse, Truck, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { industries } from '../mockData';

const Industries = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      'calculator': Calculator,
      'heart-pulse': HeartPulse,
      'truck': Truck,
      'users': Users
    };
    return icons[iconName] || Calculator;
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Industries We Serve"
        description="Specialized recruiting for accounting, healthcare, logistics, and HR & people compliance industries. Omnia Paratus Talent Solutions provides expert staffing services with deep market knowledge in each sector."
        keywords="accounting recruiters, healthcare recruiters, logistics recruiters, HR recruiters, people compliance recruiters, industry-specific staffing, specialized recruiting agency, professional placement services"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Industries We Serve
          </h1>
        </div>
      </section>

      {/* Industries Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our specialized focus allows us to develop deep market knowledge,<br />maintain extensive networks, and understand the unique challenges<br />and opportunities within each industry we serve.
            </p>
          </div>

          <div className="space-y-16">
            {industries.map((industry, index) => {
              const IconComponent = getIcon(industry.icon);
              
              return (
                <Card key={industry.id} className="border-2 border-gray-200 hover:border-[#D4AF37] transition-all duration-300 shadow-xl hover:shadow-2xl overflow-hidden">
                  <CardContent className="p-0">
                    <div className={`grid grid-cols-1 lg:grid-cols-5 ${index % 2 === 0 ? '' : 'lg:grid-flow-dense'}`}>
                      {/* Icon Section */}
                      <div className={`lg:col-span-2 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c] p-12 flex items-center justify-center ${index % 2 === 0 ? '' : 'lg:col-start-4'}`}>
                        <div className="text-center">
                          <div className="w-32 h-32 mx-auto mb-6 bg-[#D4AF37] rounded-3xl flex items-center justify-center shadow-2xl">
                            <IconComponent className="h-16 w-16 text-[#0A1F44]" />
                          </div>
                          <h2 className="text-3xl font-bold text-white mb-4">{industry.name}</h2>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className={`lg:col-span-3 p-12 ${index % 2 === 0 ? '' : 'lg:col-start-1 lg:row-start-1'}`}>
                        <p className="text-lg text-gray-700 leading-relaxed mb-8">
                          {industry.description}
                        </p>

                        <div className="mb-8">
                          <h3 className="text-xl font-bold text-[#0A1F44] mb-4">Key Specialties</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {industry.specialties.map((specialty, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                                <span className="text-gray-700">{specialty}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <Button
                          asChild
                          size="lg"
                          className="bg-[#0A1F44] hover:bg-[#0A1F44]/90 text-white"
                        >
                          <Link to="/contact" className="flex items-center gap-2">
                            Discuss Your Needs <ArrowRight className="h-5 w-5" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Insights */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Why Industry Specialization Matters
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our focused approach delivers better results for both employers and candidates
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Deep Networks',
                description: 'Established relationships with top talent in each sector'
              },
              {
                title: 'Market Intelligence',
                description: 'Current insights on salary trends, hiring challenges, and opportunities'
              },
              {
                title: 'Technical Understanding',
                description: 'Ability to assess specialized skills and qualifications accurately'
              },
              {
                title: 'Cultural Fit',
                description: 'Understanding of industry-specific workplace cultures and values'
              }
            ].map((insight, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#D4AF37] rounded-2xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#0A1F44]">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1F44] mb-3">{insight.title}</h3>
                  <p className="text-gray-600">{insight.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Proven Results Across Industries
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { industry: 'Accounting', placements: '150+', avgTime: '18 days' },
              { industry: 'Healthcare', placements: '200+', avgTime: '21 days' },
              { industry: 'Logistics', placements: '120+', avgTime: '15 days' },
              { industry: 'HR & People Compliance', placements: '80+', avgTime: '25 days' }
            ].map((stat, index) => (
              <Card key={index} className="border-2 border-[#D4AF37] shadow-lg">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-[#0A1F44] mb-6">{stat.industry}</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-4xl font-bold text-[#D4AF37] mb-1">{stat.placements}</div>
                      <div className="text-sm text-gray-600">Successful Placements</div>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div>
                      <div className="text-4xl font-bold text-[#D4AF37] mb-1">{stat.avgTime}</div>
                      <div className="text-sm text-gray-600">Avg. Time to Hire</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Tap Into Our Industry Expertise?
          </h2>
          <p className="text-xl text-gray-200 mb-10">
            Let's discuss how our specialized knowledge can help you achieve your hiring goals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#D4AF37]/90 text-lg px-8 py-6 shadow-xl"
            >
              <Link to="/contact" className="flex items-center gap-2">
                Get Started <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-[#0A1F44] text-lg px-8 py-6"
            >
              <Link to="/services">Our Services</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Industries;
